import type { User } from "oidc-client-ts";

/**
 * Mutable bridge between react-oidc-context (a React provider) and non-React
 * call sites — the openapi-ts client, SignalR factories, router beforeLoad guards.
 *
 * Synced render-phase by `useAuthBridgeSync` so callers reading from
 * `beforeLoad` (which runs outside React) always see the same state the
 * component tree just rendered with.
 */
export type AuthBridge = {
  getAccessToken(): string | undefined;
  isAuthenticated(): boolean;
  isLoading(): boolean;
  getUser(): User | null;
  signinRedirect(): Promise<unknown> | unknown;
  signoutRedirect(): Promise<unknown> | unknown;
};

let currentUser: User | null = null;
let currentAuthed = false;
// Start true so any beforeLoad that fires before the AuthProvider has had a
// chance to hydrate doesn't bounce an authenticated user to /login.
let currentLoading = true;
// Dev-bypass mode doesn't go through oidc-client-ts, so there's no `User`
// with a real `access_token`. Stash a sentinel here so `getAccessToken()`
// still returns something — keeps `apiFetch` / SignalR sending an
// Authorization header in the same shape they do in OIDC mode, and the
// API-side `DevBypassAuthenticationHandler` ignores the value anyway.
let overrideToken: string | undefined;
let currentSignin: (() => Promise<unknown> | unknown) | undefined;
let currentSignout: (() => Promise<unknown> | unknown) | undefined;

export const authBridge: AuthBridge = {
  getAccessToken: () => currentUser?.access_token ?? overrideToken,
  isAuthenticated: () => currentAuthed,
  isLoading: () => currentLoading,
  getUser: () => currentUser,
  signinRedirect: () => currentSignin?.(),
  signoutRedirect: () => currentSignout?.(),
};

export function setAuthState(
  user: User | null,
  isAuthenticated: boolean,
  isLoading: boolean,
  signin: () => Promise<unknown> | unknown,
  signout: () => Promise<unknown> | unknown,
) {
  currentUser = user;
  currentAuthed = isAuthenticated;
  currentLoading = isLoading;
  currentSignin = signin;
  currentSignout = signout;
}

/** Dev-bypass entry point — see `useSession` (DEV_AUTH branch). */
export function setDevAuthState(token: string) {
  overrideToken = token;
  currentUser = null;
  currentAuthed = true;
  currentLoading = false;
  currentSignin = () => {
    // No-op — `/` route's beforeLoad sees `isAuthenticated()` true and never calls this.
  };
  currentSignout = () => window.location.reload();
}
